import React, { useState } from "react";
import { Typography } from "@mui/material";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllRolesViewModel } from "./AllRolesViewModel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import AddRole from "./AddRole";
import { useRbac } from "../../hooks/RbacContext";
import { Alert, Box } from "@mui/material";
import { THEME, MainCard } from "./Roles.style";

const AllRoles = () => {
  const {
    roles,
    loading,
    error,
    handleDelete,
    createRole,
    updateRole,
    refetch,
    handleSearch,
  } = useAllRolesViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    { id: "role_name", label: "Name" },
    { id: "role_description", label: "Description", align: "left" },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
  ];

  const fixedColumns = { left: [], right: ["is_active", "actions"] };

  const handleAddClient = () => {
    setSelectedRole(null);
    setIsAddRoleOpen(true);
  };

  const handleEditClient = (client) => {
    setSelectedRole(client);
    setIsAddRoleOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddRoleOpen(false);
    setSelectedRole(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedRole) {
        await updateRole(selectedRole.role_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Role updated successfully",
        });
      } else {
        await createRole(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Role added successfully",
        });
      }
      handleCloseDialog();
      await refetch();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => setSuccessModal(null);

  const handleDeleteClick = (role) =>
    setDeleteConfirmation({ role, open: true });

  const handleDeleteConfirm = async () => {
    const role = deleteConfirmation.role;
    try {
      showLoading();
      await handleDelete(role);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Role deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete Role",
      });
    } finally {
      hideLoading();
    }
  };

  // Actions gated by RBAC, otherwise behavior remains identical for authorized users
  const getActions = (client) => {
    const actions = [];

    if (checkAccess("role", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClient,
        sx: {
          color: THEME.primary,
          "&:hover": {
            backgroundColor: THEME.primaryRgba(0.08),
          },
        },
      });
    }
    if (checkAccess("role", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (t) =>
              t.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <MainCard sx={{ p: 1.5 }}>
        <Alert severity="error" sx={{ borderRadius: 8 }}>{error}</Alert>
      </MainCard>
    );
  }

  // Page-level guard: require view permission
  if (!checkAccess("role", "view")) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view Roles.
        </Alert>
      </Box>
    );
  }

  return (
    <>
    <MainCard sx={{ height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <CustomTable
          columns={columns}
          data={roles}
          onAdd={handleAddClient}
          showActions={true}
          showAddButton={checkAccess("role", "add")}
          addButtonIcon={<PersonAddIcon />}
          showSearch={true}
          showColumnVisibility={false}
          pageSize={10}
          stickyHeader={true}
          fixedColumns={fixedColumns}
          actions={getActions}
          title="All Roles"
          subtitle="Manage and monitor all roles"
          addButtonText="Add Role"
          alignment="left"
          fontSize="12px"
          showFilters={true}
          compact={true}
        />
      </Box>
    </MainCard>

      {/* Add/Edit Role dialog (opening it still depends on the button/action visibility above) */}
      <AddRole
        open={isAddRoleOpen}
        onClose={handleCloseDialog}
        initialData={selectedRole}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}


      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Role"
          message={`Are you sure you want to delete role "${deleteConfirmation.role.role_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default AllRoles;
